import { useState, useCallback } from 'react'
import Link from 'next/link'

import styles from './Qa.module.scss'

import AccordionIconComponent from '../../icons/AccordionIconComponent'
import { useTranslation } from 'next-i18next'

interface Props {
  locationName: string
  id: number
  ignoreIds: number[]
  useWithAreaTranslations?: boolean
}

const Qa: React.VFC<Props> = ({
  locationName,
  id,
  ignoreIds,
  useWithAreaTranslations = false,
}: Props) => {
  const { t } = useTranslation()

  const getQuestionDescription = (
    defaultKey: string,
    withAreaKey: string,
    options?: Record<string, unknown>
  ) => t(useWithAreaTranslations ? withAreaKey : defaultKey, options)

  const qaItems = [
    //No.01
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_0'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_0',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_WITH_AREA_0'
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_0'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_0'),
      },
    },
    //No.02
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_1'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_1',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_WITH_AREA_1',
          { locationName: locationName }
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_1'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_1'),
      },
    },
    //No.03
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_2'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_2',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_WITH_AREA_2',
          { locationName: locationName }
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_2'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_2', { locationName: locationName }),
      },
    },
    //No.04
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_3'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_3',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_WITH_AREA_3'
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_3'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_3'),
      },
    },
    //No.05
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_4'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_4',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_WITH_AREA_4'
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_4'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_4'),
      },
    },
    //No.06
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_5'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_5',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_WITH_AREA_5'
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_5'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_5'),
      },
    },
    //No.07
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_6'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_6',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_6_WITH_AREA',
          { locationName: locationName }
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_6'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_6', { locationName: locationName }),
      },
    },
    //No.08
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_7', { locationName: locationName }),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_7',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_7_WITH_AREA',
          { locationName: locationName }
        ),
        isOpen: false,
        isDisplay: !ignoreIds.includes(id),
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_7'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_7', { locationName: locationName }),
      },
    },
    //No.09
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_8'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_8',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_8_WITH_AREA',
          { locationName: locationName }
        ),
        isOpen: false,
        isDisplay: !ignoreIds.includes(id),
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_8'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_8', { locationName: locationName }),
      },
    },
    //No.10
    {
      question: {
        title: t('AREAS.QA.CONTENTS.QUESTION.TITLE_9'),
        description: getQuestionDescription(
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_9',
          'AREAS.QA.CONTENTS.QUESTION.DESCRIPTION_9_WITH_AREA',
          { locationName: locationName }
        ),
        isOpen: false,
      },
      answer: {
        title: t('AREAS.QA.CONTENTS.ANSWER.TITLE_9'),
        description: t('AREAS.QA.CONTENTS.ANSWER.DESCRIPTION_9'),
      },
    },
  ]

  const [qaList, setQaList] = useState(qaItems)

  const _onClickAccordion = useCallback((index) => {
    const updatedList = [...qaList]
    updatedList[index].question.isOpen = !updatedList[index].question.isOpen

    setQaList(updatedList)
  }, [])

  return (
    <section className={styles.qa}>
      <ul className={styles.qaList}>
        {qaList.map((qa, index) => {
          if (qa.question.isDisplay === false) {
            return null
          }
          return (
            <li className={styles.qaListItem} key={index}>
              <div className={styles.qaListItemQuestion} onClick={() => _onClickAccordion(index)}>
                <div className={styles.qaListItemContent}>
                  <p className={styles.qaListItemContentTitle}>{qa.question.description}</p>
                </div>
                <div className={styles.qaListItemQuestionTrigger}>
                  <AccordionIconComponent
                    isOpenAccordion={qa.question.isOpen}
                    accordionStyle={styles.accordionIcon}
                  />
                </div>
              </div>
              {qa.question.isOpen && (
                <div className={styles.qaListItemAnswer}>
                  <div className={styles.qaListItemContent}>
                    <p className={styles.qaListItemContentTitle}>{qa.answer.title}</p>
                    <p className={styles.qaListItemContentDesc}>
                      {qa.answer.description
                        .split(/(https?:\/\/[\w!\?/\+\-_~=;\.,\*&@#\$%\(\)'\[\]]+)/)
                        .map((v, i) =>
                          i & 1 ? (
                            <Link key={i} href={v} passHref legacyBehavior>
                              <a target="_blank" rel="noreferrer" className={styles.link}>
                                {v}
                              </a>
                            </Link>
                          ) : (
                            v
                          )
                        )}
                    </p>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default Qa
